import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { Stack } from "components/icons/generated";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanDeleteDeployment from "shared/Templates/access/useCanDeleteDeployment";
import {
  BlueprintDeployment,
  BlueprintDeploymentState,
  BlueprintVersionedGroup,
} from "types/generated";
import { showDeleteConfirmation } from "views/Account/Template/Deployments/DeleteConfirmation";
import { AccountContext } from "views/AccountWrapper";
import DeploymentProgressCallout from "components/DeploymentProgressCallout";

import DeploymentStateBadge from "../../Template/Deployments/StateBadge";
import TemplateDeploymentOutputsEmpty from "../../TemplateDeployment/Outputs/Empty";
import { getDeploymentOutputs } from "../../TemplateDeployment/Outputs/helpers";
import TemplateDeploymentOutputsTable from "../../TemplateDeployment/Outputs/Table";

type PageLayoutProps = {
  deployment: BlueprintDeployment;
  template: BlueprintVersionedGroup;
};

const PageLayout = ({ deployment, template }: PageLayoutProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const navigate = useNavigate();

  const deleteAccess = useCanDeleteDeployment()(deployment, template.space.id);

  useTitle(`Templates · ${deployment.name} · ${accountName}`);

  const outputs = useMemo(() => getDeploymentOutputs(deployment), [deployment]);

  const isInProgress =
    deployment.state === BlueprintDeploymentState.InProgress ||
    deployment.state === BlueprintDeploymentState.None;

  const handleDelete = useCallback(() => {
    if (!deployment) {
      return;
    }

    showDeleteConfirmation({
      name: deployment.name,
      id: deployment.id,
      blueprintID: template.id,
      onDelete: () => {
        navigate(`/templates/template/${template.id}`);
      },
    });
  }, [deployment, navigate, template]);

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{deployment.name}</ViewHeaderTitle>
            <DeploymentStateBadge state={deployment.state} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <DropdownMenuEllipsis tooltip="Deployment actions" dotsSize="medium">
              <CopyFieldDropdownMenuItem title="Copy ID" value={deployment.id} />
              <DropdownMenuItem
                onAction={handleDelete}
                danger
                isDisabled={!deleteAccess.result}
                tooltip={deleteAccess.message}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <MetaInfoListItem icon={Stack}>{deployment.blueprintVersion.version}</MetaInfoListItem>

            <TimestampMetaInfo timestamp={deployment.createdAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
      </ViewHeader>
      {isInProgress && <DeploymentProgressCallout />}

      {outputs.length === 0 ? (
        <TemplateDeploymentOutputsEmpty />
      ) : (
        <TemplateDeploymentOutputsTable outputs={outputs} />
      )}
    </>
  );
};

export default PageLayout;
